import React, { useState } from "react";
import { 
    Card,
    Typography,
    Row,
    Col,
    Descriptions,
    Button,
    Form,
    Input,
    message,
    Space,
    Tag,
    Avatar
} from 'antd';
import { 
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    TagOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useGetMyProfileQuery, useUpdateUserProfileMutation, useChangePasswordMutation } from '../../services/nestjs-base-api';
import { Utils } from '../../utils/utils';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { LockOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const MyProfile: React.FunctionComponent = () => {
    const { user } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    
    const { data: profile, refetch } = useGetMyProfileQuery({
        jwtToken: user!.jwtToken,
    });
    
    const [updateUserProfile, { isLoading: isUpdating }] = useUpdateUserProfileMutation();
    const [changePassword, { isLoading: isChangingPasswordLoading }] = useChangePasswordMutation();

    const handleEdit = () => {
        form.setFieldsValue({
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.email || '',
            phoneNumber: profile?.phoneNumber || '',
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        form.resetFields();
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            await updateUserProfile({
                jwtToken: user!.jwtToken,
                payload: {
                    firstName: values.firstName || undefined,
                    lastName: values.lastName || undefined,
                    email: values.email,
                    phoneNumber: values.phoneNumber || undefined,
                },
            }).unwrap();

            message.success('Profile updated successfully');
            setIsEditing(false);
            refetch(); // Refresh the profile data
        } catch (error: any) {
            if (error?.data?.message) {
                message.error(error.data.message);
            } else {
                message.error('Failed to update profile');
            }
        }
    };

    const handleChangePassword = async () => {
        try {
            const values = await passwordForm.validateFields();

            console.log('[ChangePassword] Submitting password change');

            // Normalize passwords to handle Unicode encoding differences (Mac vs Windows)
            const normalizedCurrentPassword = values.currentPassword?.normalize('NFC');
            const normalizedNewPassword = values.newPassword?.normalize('NFC');

            if (normalizedCurrentPassword !== values.currentPassword) {
                console.log('[ChangePassword] Current password normalized from NFD to NFC');
            }
            if (normalizedNewPassword !== values.newPassword) {
                console.log('[ChangePassword] New password normalized from NFD to NFC');
            }

            await changePassword({
                jwtToken: user!.jwtToken,
                currentPassword: normalizedCurrentPassword,
                newPassword: normalizedNewPassword,
            }).unwrap();

            message.success('Password changed successfully');
            setIsChangingPassword(false);
            passwordForm.resetFields();
        } catch (error: any) {
            console.error('[ChangePassword] Error:', error);
            if (error?.data?.message) {
                message.error(error.data.message);
            } else {
                message.error('Failed to change password');
            }
        }
    };

    const handleCancelPasswordChange = () => {
        setIsChangingPassword(false);
        passwordForm.resetFields();
    };

    const getDisplayName = () => {
        if (profile?.firstName || profile?.lastName) {
            return `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();
        }
        return profile?.email || 'User';
    };

    const getInitials = () => {
        if (profile?.firstName || profile?.lastName) {
            const first = profile?.firstName?.charAt(0) || '';
            const last = profile?.lastName?.charAt(0) || '';
            return `${first}${last}`.toUpperCase();
        }
        return profile?.email?.charAt(0).toUpperCase() || 'U';
    };

    const getRoleDisplay = (roles: string | string[]) => {
        try {
            const rolesArray = typeof roles === 'string' ? JSON.parse(roles) : roles;
            return rolesArray.map((role: string) => {
                // Use the Utils function to get the proper readable role name
                return Utils.readableUserRole(role as UserRoleEnum);
            }).join(', ');
        } catch {
            return typeof roles === 'string' ? roles : roles.join(', ');
        }
    };

    if (!profile) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>My Profile</Title>
                {!isEditing && (
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />}
                        onClick={handleEdit}
                    >
                        Edit Profile
                    </Button>
                )}
            </div>

            {isEditing ? (
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                    >
                        <Row gutter={[24, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="First Name"
                                    name="firstName"
                                    rules={[
                                        { max: 50, message: 'First name must not exceed 50 characters' }
                                    ]}
                                >
                                    <Input 
                                        prefix={<UserOutlined />}
                                        placeholder="Enter your first name"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Last Name"
                                    name="lastName"
                                    rules={[
                                        { max: 50, message: 'Last name must not exceed 50 characters' }
                                    ]}
                                >
                                    <Input 
                                        prefix={<UserOutlined />}
                                        placeholder="Enter your last name"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Email Address"
                                    name="email"
                                    rules={[
                                        { required: true, message: 'Please enter your email address' },
                                        { type: 'email', message: 'Please enter a valid email address' }
                                    ]}
                                >
                                    <Input 
                                        prefix={<MailOutlined />}
                                        placeholder="your.email@company.com"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Phone Number"
                                    name="phoneNumber"
                                    rules={[
                                        { max: 20, message: 'Phone number must not exceed 20 characters' }
                                    ]}
                                >
                                    <Input 
                                        prefix={<PhoneOutlined />}
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={isUpdating}
                            >
                                Save Changes
                            </Button>
                            <Button 
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Card>
            ) : (
                <>
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Card title="Personal Information">
                            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                                <Avatar size={64} style={{ backgroundColor: '#F59E0B' }}>
                                    {getInitials()}
                                </Avatar>
                                <div>
                                    <Title level={5} style={{ margin: 0 }}>
                                        {getDisplayName()}
                                    </Title>
                                    <Text type="secondary">{profile.email}</Text>
                                </div>
                            </div>
                            
                            <Descriptions column={1} size="middle" colon={false}>
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <UserOutlined />
                                            <Text strong>First Name</Text>
                                        </Space>
                                    }
                                >
                                    <Text>{profile.firstName || <Text type="secondary">Not set</Text>}</Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <UserOutlined />
                                            <Text strong>Last Name</Text>
                                        </Space>
                                    }
                                >
                                    <Text>{profile.lastName || <Text type="secondary">Not set</Text>}</Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <MailOutlined />
                                            <Text strong>Email Address</Text>
                                        </Space>
                                    }
                                >
                                    <Text>
                                        <a href={`mailto:${profile.email}`}>
                                            {profile.email}
                                        </a>
                                    </Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <PhoneOutlined />
                                            <Text strong>Phone Number</Text>
                                        </Space>
                                    }
                                >
                                    <Text>
                                        {profile.phoneNumber ? (
                                            <a href={`tel:${profile.phoneNumber}`}>
                                                {profile.phoneNumber}
                                            </a>
                                        ) : (
                                            <Text type="secondary">Not set</Text>
                                        )}
                                    </Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <TagOutlined />
                                            <Text strong>Role</Text>
                                        </Space>
                                    }
                                >
                                    <Tag color="blue">
                                        {getRoleDisplay(profile.roles)}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                    
                    <Col xs={24} lg={8}>
                        <Card title="Account Information">
                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                <div>
                                    <Text strong>Organisation</Text>
                                    <br />
                                    <Text>{profile.organisation?.name}</Text>
                                </div>
                                
                                <div>
                                    <Text strong>Account Status</Text>
                                    <br />
                                    <Tag color="green">{profile.status}</Tag>
                                </div>
                                
                                {profile.createdAt && (
                                    <div>
                                        <Text strong>Member Since</Text>
                                        <br />
                                        <Text type="secondary">
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {new Date(profile.createdAt).toLocaleDateString()}
                                        </Text>
                                    </div>
                                )}
                                
                                <Button 
                                    type="default" 
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    block
                                    style={{ marginTop: 16 }}
                                >
                                    Edit Profile
                                </Button>
                            </Space>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                    <Col xs={24}>
                        <Card
                            title={
                                <Space>
                                    <LockOutlined />
                                    <span>Change Password</span>
                                </Space>
                            }
                        >
                            {!isChangingPassword ? (
                                <div>
                                    <Text type="secondary">
                                        Keep your account secure by changing your password regularly.
                                    </Text>
                                    <div style={{ marginTop: 16, padding: 16, background: '#f0f2f5', borderRadius: 8 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 12 }}>Password Requirements:</Text>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span>🔐</span>
                                                <Text>At least 8 characters long</Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span>💪</span>
                                                <Text>Mix of letters, numbers & symbols</Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span>🛡️</span>
                                                <Text>Unique to your BrandScience.ai account</Text>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<LockOutlined />}
                                        onClick={() => setIsChangingPassword(true)}
                                        style={{ marginTop: 16 }}
                                    >
                                        Change Password
                                    </Button>
                                </div>
                            ) : (
                                <Form
                                    form={passwordForm}
                                    layout="vertical"
                                    onFinish={handleChangePassword}
                                >
                                    <Row gutter={[24, 16]}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label="Current Password"
                                                name="currentPassword"
                                                rules={[
                                                    { required: true, message: 'Please enter your current password' }
                                                ]}
                                            >
                                                <Input.Password
                                                    prefix={<LockOutlined />}
                                                    placeholder="Enter your current password"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={[24, 16]}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label="New Password"
                                                name="newPassword"
                                                rules={[
                                                    { required: true, message: 'Please enter your new password' },
                                                    { min: 8, message: 'Password must be at least 8 characters' },
                                                    {
                                                        pattern: /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/,
                                                        message: 'Password must contain letters, numbers, and symbols'
                                                    }
                                                ]}
                                            >
                                                <Input.Password
                                                    prefix={<LockOutlined />}
                                                    placeholder="Enter your new password"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label="Confirm New Password"
                                                name="confirmPassword"
                                                dependencies={['newPassword']}
                                                rules={[
                                                    { required: true, message: 'Please confirm your new password' },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value || getFieldValue('newPassword') === value) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error('Passwords do not match'));
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Input.Password
                                                    prefix={<LockOutlined />}
                                                    placeholder="Confirm your new password"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            loading={isChangingPasswordLoading}
                                        >
                                            Change Password
                                        </Button>
                                        <Button
                                            icon={<CloseOutlined />}
                                            onClick={handleCancelPasswordChange}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card>
                    </Col>
                </Row>
                </>
            )}
        </div>
    );
}

export default MyProfile;